import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, CheckCircle } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import './ContactForm.css';

const ContactForm = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const offices = [
    {
      cityKey: 'kocaeliCity',
      address: 'Hasan Atakan Caddesi No 32 Ofis 2',
      district: 'Kuruçeşme - Kocaeli',
      phone: '0 262 526 03 06',
      phoneLink: 'tel:+902625260306'
    },
    {
      cityKey: 'istanbulCity',
      address: 'Kağıthane Nurtepe Ece Sokak',
      district: 'Osman Paşa Genyap İş Merkezi C Blok No:8 Ofis:12',
      location: 'Kağıthane, İstanbul',
      phone: null,
      phoneLink: null
    }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // WhatsApp mesajını oluştur
    const whatsappPhone = '905334924678';
    const message = `*Gedik Group İletişim Formu*
    
👤 *Ad Soyad:* ${formData.name}
📧 *E-posta:* ${formData.email}
📱 *Telefon:* ${formData.phone}
💬 *Mesaj:* ${formData.message}`;

    // URL encode the message
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${whatsappPhone}?text=${encodedMessage}`;
    
    // Bilgileri kaydet (ileride backend'e göndermek için)
    const contactData = {
      ...formData,
      timestamp: new Date().toISOString(),
      source: 'www.gedikgroup.tr - İletişim Formu'
    };
    
    console.log('📬 Yeni İletişim Formu:', contactData);
    
    try {
      const existingContacts = JSON.parse(localStorage.getItem('gedik_contacts') || '[]');
      existingContacts.push(contactData);
      localStorage.setItem('gedik_contacts', JSON.stringify(existingContacts));
    } catch (error) {
      console.error('❌ Kayıt hatası:', error);
    }
    
    // WhatsApp'a yönlendir
    window.open(whatsappUrl, '_blank');
    
    // Formu temizle
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  return (
    <section className="contact-form-section" id="contact">
      <div className="contact-form-container">
        {/* Header */}
        <motion.div 
          className="contact-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="contact-title">{t('contact.title')}</h2>
          <p className="contact-subtitle">{t('contact.subtitle')}</p>
          <div className="contact-divider"></div>
        </motion.div>

        {/* Main Grid: Offices + Form */}
        <div className="contact-main-grid">
          {/* Left Side - Offices */}
          <motion.div
            className="offices-column"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {offices.map((office, index) => (
              <div key={index} className="office-card">
                <div className="office-header">
                  <div className="office-icon">
                    <MapPin size={24} />
                  </div>
                  <h3 className="office-city">{t(`contact.${office.cityKey}`)} {t('contact.office')}</h3>
                  <div className="office-badge">
                    {index === 0 ? t('contact.mainOffice') : t('contact.branchOffice')}
                  </div>
                </div>

                <div className="office-details">
                  <div className="office-detail-item">
                    <MapPin size={16} className="detail-icon" />
                    <div className="detail-text">
                      <p className="detail-address">{office.address}</p>
                      <p className="detail-district">{office.district}</p>
                      {office.location && <p className="detail-location">{office.location}</p>}
                    </div>
                  </div>

                  {office.phone && (
                    <div className="office-detail-item">
                      <Phone size={16} className="detail-icon" />
                      <a href={office.phoneLink} className="detail-phone">
                        {office.phone}
                      </a>
                    </div>
                  )}

                  <div className="office-detail-item">
                    <Clock size={16} className="detail-icon" />
                    <div className="detail-text">
                      <p className="detail-hours">{t('contact.workingHours')}</p>
                      <p className="detail-hours-time">{t('contact.workingHoursTime')}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Email Info */}
            <div className="email-card">
              <Mail size={20} className="email-icon" />
              <div className="email-text">
                <p className="email-label">{t('contact.emailLabel')}</p>
                <a href="mailto:info@gedikgroup.tr" className="email-value">info@gedikgroup.tr</a>
              </div>
            </div>
          </motion.div>

          {/* Right Side - Contact Form */}
          <motion.div
            className="form-column"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="contact-form-wrapper">
              <h3 className="form-title">{t('contact.formTitle')}</h3>
              <p className="form-description">{t('contact.formDescription')}</p>

              {!isSubmitted ? (
                <form className="contact-form" onSubmit={handleSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder={t('contact.namePlaceholder')}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={t('contact.emailPlaceholder')}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={t('contact.phonePlaceholder')}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={t('contact.messagePlaceholder')}
                      rows="5"
                      required
                    />
                  </div>

                  <button type="submit" className="contact-form-submit whatsapp-button">
                    <FaWhatsapp size={22} />
                    {t('contact.submit')}
                  </button>
                </form>
              ) : (
                <motion.div
                  className="success-message"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <CheckCircle size={60} />
                  <h3>{t('contact.successTitle')}</h3>
                  <p>{t('contact.success')}</p>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
